import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HelpCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, UNITS } from "@/lib/api";
import { brl, parseNum, numStr } from "@/lib/format";
import { Button, Chip, Field, Input } from "@/components/kit";

function CreateForm({ item, categories, defaultCategoryId, onCancel, onCreated }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: item.description || "", category_id: defaultCategoryId || "", unit: UNITS.includes(item.unit) ? item.unit : "un", qty: numStr(item.qty), total: numStr(item.total) });
  const [err, setErr] = useState("");
  const qty = parseNum(f.qty);
  const total = parseNum(f.total);
  const m = useMutation({
    mutationFn: () => api("/products", { method: "POST", body: { name: f.name, category_id: f.category_id, unit: f.unit, unit_cost: qty ? total / qty : 0, active: true } }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success(`Produto "${p.name}" cadastrado`);
      onCreated({ product_id: p.id, product_name: p.name, unit: p.unit, qty, total });
    },
    onError: (e) => setErr(e.message),
  });
  const save = () => {
    if (!f.name.trim()) return setErr("Informe o nome do produto");
    if (!f.category_id) return setErr("Selecione a categoria");
    if (!qty || !total) return setErr("Informe quantidade e valor do item");
    m.mutate();
  };
  const id = item.description?.slice(0, 12).replace(/\W/g, "") || "x";
  return (
    <div className="flex flex-col gap-3 rounded-xl p-3 border" style={{ borderColor: "var(--border)" }} data-testid={`unmatched-form-${id}`}>
      <div className="caption">Lido na nota: {item.description}</div>
      <Field label="Nome do produto"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`unmatched-name-${id}`} /></Field>
      <div className="label">Categoria</div>
      <div className="scroll-x">{categories.map((c) => <Chip key={c.id} active={f.category_id === c.id} onClick={() => setF({ ...f, category_id: c.id })} data-testid={`unmatched-category-${c.id}`}>{c.name}</Chip>)}</div>
      <div className="label">Unidade</div>
      <div className="scroll-x">{UNITS.map((u) => <Chip key={u} active={f.unit === u} onClick={() => setF({ ...f, unit: u })} data-testid={`unmatched-unit-${u}`}>{u}</Chip>)}</div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={`Qtd (${f.unit})`}><Input inputMode="decimal" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid={`unmatched-qty-${id}`} /></Field>
        <Field label="Valor (R$)"><Input inputMode="decimal" value={f.total} onChange={(e) => setF({ ...f, total: e.target.value })} data-testid={`unmatched-total-${id}`} /></Field>
      </div>
      <div className="caption" data-testid={`unmatched-unit-cost-${id}`}>Custo inicial: {brl(qty ? total / qty : 0)}/{f.unit}</div>
      {err && <div className="caption" style={{ color: "var(--danger)" }}>{err}</div>}
      <div className="flex gap-2 justify-end">
        <Button variant="secondary" size="sm" onClick={onCancel} data-testid={`unmatched-cancel-${id}`}>Cancelar</Button>
        <Button size="sm" loading={m.isPending} onClick={save} data-testid={`unmatched-save-${id}`}>Cadastrar e incluir</Button>
      </div>
    </div>
  );
}

export default function UnmatchedItems({ items, categories, defaultCategoryId, onCreated, onDismiss }) {
  const [open, setOpen] = useState(null);
  if (!items.length) return null;
  return (
    <div className="flex flex-col gap-2 rounded-xl p-3" style={{ background: "var(--accent-soft)" }} data-testid="unmatched-items-section">
      <div className="flex items-center gap-2"><HelpCircle size={16} style={{ color: "var(--accent)" }} /><span className="label">{items.length} item(ns) da nota sem produto cadastrado</span></div>
      {items.map((it, i) =>
        open === it.uid ? (
          <CreateForm key={it.uid} item={it} categories={categories} defaultCategoryId={defaultCategoryId} onCancel={() => setOpen(null)} onCreated={(line) => { setOpen(null); onCreated(i, line); }} />
        ) : (
          <div key={it.uid} className="flex items-center gap-2" data-testid={`unmatched-item-${i}`}>
            <span className="flex-1 text-sm truncate">{it.description} · {brl(it.total)}</span>
            <Button size="sm" variant="secondary" icon={Plus} onClick={() => setOpen(it.uid)} data-testid={`unmatched-create-${i}`}>Cadastrar</Button>
            <Button size="sm" variant="ghost" onClick={() => onDismiss(i)} data-testid={`unmatched-dismiss-${i}`}>Ignorar</Button>
          </div>
        ),
      )}
    </div>
  );
}
